import { createClient } from '@supabase/supabase-js';
import type { User } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file or Vercel project settings.');
}

// Create Supabase client with retry configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: false,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
  global: {
    fetch: (...args) => {
      return fetch(...args).catch(err => {
        // We moeten een custom logger gebruiken als 'logger' niet bestaat
        console.error('Supabase fetch error:', err);
        throw err;
      });
    }
  },
  db: {
    schema: 'public'
  }
});

// Helper function to validate and fetch employee data
const getEmployeeData = async (userId: string) => {
  try {
    // First try to get the employee record by user ID
    const { data: employeeData, error: employeeError } = await supabase
      .from('employees')
      .select('id, company_id, role')
      .eq('user_id', userId)
      .maybeSingle();

    if (employeeError) {
      console.error('Error fetching employee:', employeeError);
      throw new Error('Kon werknemersgegevens niet ophalen');
    }

    // If employee found by user ID, validate and return with company data
    if (employeeData) {
      if (!employeeData.company_id) {
        console.error('Employee has no company_id:', employeeData);
        throw new Error('Werknemer heeft geen bedrijf toegewezen');
      }

      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', employeeData.company_id)
        .single();

      if (companyError) {
        throw new Error('Kon bedrijfsgegevens niet ophalen');
      }

      return {
        ...employeeData,
        companies: companyData
      };
    }

    // No employee found by user ID, try to find by email
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    
    if (!user) throw new Error('Geen gebruiker gevonden');

    // Check if an employee with this email already exists
    const { data: existingEmployee, error: existingError } = await supabase
      .from('employees')
      .select('id, company_id, role')
      .eq('email', user.email!)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing employee:', existingError);
      throw new Error('Kon bestaande werknemer niet controleren');
    }

    if (existingEmployee) {
      if (!existingEmployee.company_id) {
        console.error('Existing employee has no company_id:', existingEmployee);
        throw new Error('Bestaande werknemer heeft geen bedrijf toegewezen');
      }

      const { error: updateError } = await supabase
        .from('employees')
        .update({ user_id: userId })
        .eq('id', existingEmployee.id)

      if (updateError) {
        throw new Error('Kon werknemers-ID niet bijwerken');
      }

      // Get company name
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', existingEmployee.company_id)
        .single();

      if (companyError) {
        throw new Error('Kon bedrijfsgegevens niet ophalen');
      }

      return {
        ...existingEmployee,
        companies: companyData
      };
    }

    // No employee with this email exists, create new company and employee
    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .insert({
        name: user.email?.split('@')[0] ?? 'Nieuw Bedrijf',
        settings: {},
        user_id: userId
      })
      .select()
      .single();

    if (companyError) {
      throw new Error('Kon geen nieuw bedrijf aanmaken');
    }

    // Create new employee record
    const { data: newEmployee, error: createError } = await supabase
      .from('employees')
      .insert({
        user_id: userId,
        name: user.email?.split('@')[0] ?? 'Nieuwe Medewerker',
        email: user.email!,
        role: 'admin',
        company_id: companyData.id,
        status: 'active'
      })
      .select('id, company_id, role')
      .single();

    if (createError) {
      throw new Error('Kon geen nieuwe werknemer aanmaken');
    }

    return {
      ...newEmployee,
      companies: companyData
    };
  } catch (error) {
    throw error;
  }
};

// Helper function to update user metadata
const updateUserMetadata = async (data: { company_id: string | null; role: string; company_name?: string }) => {
  try {
    const { error: updateError } = await supabase.auth.updateUser({
      data: {
        company_id: data.company_id,
        role: data.role,
        company_name: data.company_name ?? undefined
      }
    });

    if (updateError) {
      throw new Error('Kon gebruikersgegevens niet bijwerken');
    }

    // Verify the update
    const { data: { user }, error: verifyError } = await supabase.auth.getUser();
    if (verifyError) throw verifyError;

    if (!user?.user_metadata?.company_id) {
      throw new Error('Metadata update verificatie mislukt');
    }

    return user;
  } catch (error) {
    throw error;
  }
};

export const auth = {
  signIn: async (email: string, password: string) => {
    try {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) throw signInError;

      if (signInData.user) {
        // Fetch and validate employee data
        const employeeData = await getEmployeeData(signInData.user.id);
        
        // Update user metadata with company info
        await updateUserMetadata({
          company_id: employeeData.company_id as string,
          role: employeeData.role,
          company_name: employeeData.companies?.name ?? undefined
        });

        return signInData;
      }

      return signInData;
    } catch (error) {
      throw error;
    }
  },

  signOut: async () => {
    try {
      // Clear all local storage
      localStorage.clear();
      sessionStorage.clear();

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Force reload the page to clear all state
      window.location.href = '/auth';
    } catch (error) {
      // Even if there's an error, try to force a clean state
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/auth';
    }
  },

  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      
      if (session) {
        // Session found, no logging needed
      }
      
      return session;
    } catch (error) {
      return null;
    }
  },

  getUser: async (): Promise<User | null> => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        // Handle missing session gracefully by returning null instead of throwing
        if (error.message === 'Auth session missing!') {
          return null;
        }
        throw error;
      }

      if (!user) {
        return null;
      }

      // If user exists but doesn't have company_id in metadata, fetch and update it
      if (!user.user_metadata?.company_id) {
        // Fetch and validate employee data
        try {
          const employeeData = await getEmployeeData(user.id);
          
          // Update user metadata with company info
          await updateUserMetadata({
            company_id: employeeData.company_id,
            role: employeeData.role,
            company_name: employeeData.companies?.name ?? undefined
          });

          // Refetch user to get updated metadata
          const { data: { user: updatedUser } } = await supabase.auth.getUser();
          return updatedUser;
        } catch (employeeError) {
          return user;
        }
      }

      return user;
    } catch (error) {
      return null;
    }
  }
};